/**
 * Utilitários para gerenciamento de sessão
 */

import { SessionKeys, VendedorSession, ClienteSession } from '@types';

export class SessionUtil {
  /**
   * Define um valor na sessão
   */
  static set(key: SessionKeys | string, value: any): void {
    try {
      const serializedValue = typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, serializedValue);
    } catch (error) {
      console.error('Erro ao salvar na sessão:', error);
    }
  }

  /**
   * Obtém um valor da sessão
   */
  static get<T = any>(key: SessionKeys | string, defaultValue: T | null = null): T | null {
    try {
      const value = localStorage.getItem(key);
      if (value === null) return defaultValue;

      // Tenta fazer parse JSON
      try {
        const parsed = JSON.parse(value);
        return parsed as T;
      } catch {
        // Se não for JSON válido, retorna o valor como string
        return value as T;
      }
    } catch (error) {
      console.error('Erro ao ler da sessão:', error);
      return defaultValue;
    }
  }

  /**
   * Remove um item da sessão
   */
  static remove(key: SessionKeys | string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Erro ao remover da sessão:', error);
    }
  }

  /**
   * Limpa toda a sessão
   */
  static clear(): void {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Erro ao limpar sessão:', error);
    }
  }

  /**
   * Verifica se existe um valor na sessão
   */
  static has(key: SessionKeys | string): boolean {
    return localStorage.getItem(key) !== null;
  }

  /**
   * Salva dados do vendedor na sessão
   */
  static setVendedor(data: VendedorSession): void {
    this.set(SessionKeys.VEND_ID, data.vendId);
    this.set(SessionKeys.VEND_CODIGO, data.vendCodigo);
    this.set(SessionKeys.VEND_TIPO, data.vendTipo);
    this.set(SessionKeys.VEND_NOME, data.vendNome);
    this.set(SessionKeys.VEND_EMAIL, data.vendEmail);
    this.set(SessionKeys.IND_PEDIDO, data.indPedido);
    this.set(SessionKeys.IND_PREVISAO, data.indPrevisao);
    this.set(SessionKeys.IND_PREVISAO_ID, data.indPrevisaoId);
    
    if (data.indPrevisao !== 0 && data.indPrevisaoIni && data.indPrevisaoFim) {
      this.set(SessionKeys.IND_PREVISAO_INI, data.indPrevisaoIni);
      this.set(SessionKeys.IND_PREVISAO_FIM, data.indPrevisaoFim);
      this.set(SessionKeys.IND_PREVISAO_CONFIRMA, data.indPrevisaoConfirma);
    }
  }

  /**
   * Obtém dados do vendedor da sessão
   */
  static getVendedor(): VendedorSession | null {
    const vendCodigo = this.get<string>(SessionKeys.VEND_CODIGO);

    if (!vendCodigo) return null;

    // Garante conversão para número para evitar problemas de tipo
    const indPedido = this.get<number>(SessionKeys.IND_PEDIDO);
    const indPrevisao = this.get<number>(SessionKeys.IND_PREVISAO);
    const indPrevisaoId = this.get<number>(SessionKeys.IND_PREVISAO_ID);

    return {
      vendCodigo,
      vendId: this.get<number>(SessionKeys.VEND_ID) || 0,
      vendTipo: this.get<string>(SessionKeys.VEND_TIPO) || '',
      vendNome: this.get<string>(SessionKeys.VEND_NOME) || '',
      vendEmail: this.get<string>(SessionKeys.VEND_EMAIL) || '',
      indPedido: typeof indPedido === 'number' ? indPedido : Number(indPedido) || 0,
      indPrevisao: typeof indPrevisao === 'number' ? indPrevisao : Number(indPrevisao) || 0,
      indPrevisaoId: typeof indPrevisaoId === 'number' ? indPrevisaoId : Number(indPrevisaoId) || 0,
      indPrevisaoIni: this.get<string>(SessionKeys.IND_PREVISAO_INI) || undefined,
      indPrevisaoFim: this.get<string>(SessionKeys.IND_PREVISAO_FIM) || undefined,
      indPrevisaoConfirma: this.get<string>(SessionKeys.IND_PREVISAO_CONFIRMA) || undefined,
    };
  }

  /**
   * Salva dados do cliente na sessão
   */
  static setCliente(codigo: string, nome: string): void {
    this.set(SessionKeys.CLI_CODIGO, codigo);
    this.set(SessionKeys.CLI_NOME, nome);
  }

  /**
   * Obtém dados do cliente da sessão
   */
  static getCliente(): ClienteSession | null {
    const cliCodigo = this.get<string>(SessionKeys.CLI_CODIGO);
    
    if (!cliCodigo) return null;

    return {
      cliCodigo,
      cliNome: this.get<string>(SessionKeys.CLI_NOME) || '',
    };
  }

  /**
   * Remove dados do cliente da sessão
   */
  static clearCliente(): void {
    this.remove(SessionKeys.CLI_CODIGO);
    this.remove(SessionKeys.CLI_NOME);
  }

  /**
   * Verifica se o vendedor está autenticado
   */
  static isAuthenticated(): boolean {
    return this.has(SessionKeys.VEND_CODIGO);
  }

  /**
   * Verifica se pode fazer previsão: indPrevisao <> 0 AND indPedido = 0
   */
  static canMakePrevisao(): boolean {
    const vendedor = this.getVendedor();
    return vendedor?.indPrevisao !== 0 && vendedor?.indPedido === 0;
  }

  /**
   * Verifica se pode fazer pedido: indPrevisao <> 0 AND indPedido <> 0
   */
  static canMakePedido(): boolean {
    const vendedor = this.getVendedor();
    return vendedor?.indPrevisao !== 0 && vendedor?.indPedido !== 0;
  }

  /**
   * Obtém tipo de operação atual (previsao ou pedido)
   */
  static getOperationType(): 'previsao' | 'pedido' | null {
    if (this.canMakePrevisao()) return 'previsao';
    if (this.canMakePedido()) return 'pedido';
    return null;
  }

  /**
   * Salva última classificação acessada
   */
  static setLastClassificacao(codigo: string): void {
    this.set(SessionKeys.LAST_CLASSIFICACAO, codigo);
  }

  /**
   * Obtém última classificação acessada
   */
  static getLastClassificacao(): string | null {
    return this.get<string>(SessionKeys.LAST_CLASSIFICACAO);
  }

  /**
   * Limpa dados de navegação
   */
  static clearNavigation(): void {
    this.remove(SessionKeys.LAST_CLASSIFICACAO);
    this.remove(SessionKeys.LAST_PRODUTO);
  }

  /**
   * Faz logout - preserva carrinho de previsão se aplicável
   */
  static logout(): void {
    // Antes de limpar tudo, verifica se deve preservar o carrinho de previsão
    const vendedor = this.getVendedor();
    const isPrevisaoVendedor = vendedor?.indPedido === 0 && vendedor?.indPrevisao !== 0;

    let carrinhoBackup: string | null = null;
    let carrinhoTipoBackup: string | null = null;

    // Se é vendedor de previsão, faz backup do carrinho
    if (isPrevisaoVendedor) {
      const carrinhoTipo = this.get<string>(SessionKeys.CARRINHO_TIPO);
      if (carrinhoTipo === 'previsao') {
        carrinhoBackup = localStorage.getItem(SessionKeys.CARRINHO);
        carrinhoTipoBackup = localStorage.getItem(SessionKeys.CARRINHO_TIPO);
      }
    }

    // Limpa tudo
    this.clear();

    // Zera o indicador de previsão enviada
    localStorage.setItem('indPrevisaoEnvio', '0');

    // Restaura carrinho de previsão se aplicável
    if (carrinhoBackup && carrinhoTipoBackup) {
      localStorage.setItem(SessionKeys.CARRINHO, carrinhoBackup);
      localStorage.setItem(SessionKeys.CARRINHO_TIPO, carrinhoTipoBackup);
      console.log('Carrinho de previsão preservado no logout');
    }
  }

  /**
   * Converte string de data brasileira (DD/MM/YYYY HH:MM:SS) para objeto Date
   */
  private static parseDataBrasileira(dataStr: string): Date {
    if (!dataStr) return new Date();

    // Formato esperado: DD/MM/YYYY HH:MM:SS ou DD/MM/YYYY HH:MM
    const partes = dataStr.split(' ');
    const [dia, mes, ano] = partes[0].split('/');
    const hora = partes[1] || '00:00:00';
    const [hh, mm, ss = '00'] = hora.split(':');

    // Cria data no formato correto (ano, mes-1, dia, hora, minuto, segundo)
    return new Date(
      parseInt(ano),
      parseInt(mes) - 1, // Mês começa em 0
      parseInt(dia),
      parseInt(hh),
      parseInt(mm),
      parseInt(ss)
    );
  }

  /**
   * Verifica se a previsão está ativa (data fim maior que data atual)
   * Retorna true se a previsão está aberta para inclusão de novos produtos
   */
  static isPrevisaoAtiva(): boolean {
    const vendedor = this.getVendedor();

    // Se não tem indPrevisaoFim, considera inativo
    if (!vendedor?.indPrevisaoFim) {
      return false;
    }

    const agora = new Date();
    const dataFim = this.parseDataBrasileira(vendedor.indPrevisaoFim);

    // Retorna true se a data fim for maior que a data atual
    return dataFim.getTime() > agora.getTime();
  }
}